// React and ReactDOM imports
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import EnvironmentBanner, {
  isEnvironmentBannerVisible,
} from "./components/EnvironmentBanner";
import { AuthContextProvider, useAuth } from "./context/AuthContext";
import "./index.css";
import MenuBar from "./pages/MenuBar";
import AccessLog from "./pages/access-log/AccessLog";
import AiAgent from "./pages/ai-agent/AiAgent";
import AiReports from "./pages/ai-reports/AiReports";
import CamerasMonitor from "./pages/cameras-monitor/CamerasMonitor";
import CollectionsChange from "./pages/collections-change/CollectionsChangeRefact";
import Comparativo from "./pages/comparativo/Comparativo";
import Contatos from "./pages/contatos/Contatos";
import ControlPanel from "./pages/control-panel/ControlPanel";
import CollectionsManagerRefact from "./pages/events-manager/EventsManager";
import FlowDetectionsRefact from "./pages/flow-detections/FlowDetectionsRefact";
import ConexaoCamerasGateway from "./pages/gateway/ConexaoCamerasGateway";
import General from "./pages/general/General";
import Home from "./pages/home/Home";
import Login from "./pages/login/Login";
import ResetPassword from "./pages/login/ResetPassword";
import ListaVez from "./pages/lost-sales/ListaVez";
import OpeningHours from "./pages/opening-hours/OpeningHours";
import PeakHoursRefact from "./pages/peak-hours/PeakHoursRefact";
import PromocoesPorSku from "./pages/promocoes-sku/PromocoesPorSku";
import RevenueRefact from "./pages/revenue/RevenueRefact";
import RScoreBenchmarksManager from "./pages/settings/RScoreBenchmarksManager";
import ZonesManager from "./pages/zones/ZonesManager";
import PrivateRoute from "./utils/PrivateRoute";

if (process.env.REACT_APP_ENV === "development") {
  import("react-scan").then(({ scan }) => scan({ enabled: true }));
}

document.documentElement.classList.toggle(
  "has-env-banner",
  isEnvironmentBannerVisible()
);

const queryClient = new QueryClient({});

const root = ReactDOM.createRoot(document.getElementById("root"));

function DefaultDashboardRoute() {
  const { user } = useAuth();
  const canViewGeral = Boolean(
    user?.is_superuser || user?.is_client_admin || user?.is_admin
  );

  return <Navigate to={canViewGeral ? "/home" : "/em_alta"} replace />;
}

const protectedRoutes = [
  { router: "/em_alta", component: <Home />, menu_item_selected: "em_alta" },
  {
    router: "/comparativo",
    component: <Comparativo />,
    menu_item_selected: "comparativo",
  },
  { router: "/home", component: <General />, menu_item_selected: "home" },
  {
    router: "/geral",
    component: <Navigate to="/home" replace />,
    menu_item_selected: "home",
  },
  {
    router: "/flow_detections",
    component: <FlowDetectionsRefact />,
    menu_item_selected: "flow_detections",
  },
  {
    router: "/flow_detections_refact",
    component: <FlowDetectionsRefact />,
    menu_item_selected: "flow_detections",
  },
  {
    router: "/collections_change",
    component: <CollectionsChange />,
    menu_item_selected: "collections_change",
  },
  {
    router: "/promocoes_por_sku",
    component: <PromocoesPorSku />,
    menu_item_selected: "promocoes_por_sku",
  },
  {
    router: "/peak_hours",
    component: <PeakHoursRefact />,
    menu_item_selected: "peak_hours",
  },
  {
    router: "/events_manager",
    component: <CollectionsManagerRefact />,
    menu_item_selected: "events_manager",
  },
  {
    router: "/revenue",
    component: <RevenueRefact />,
    menu_item_selected: "revenue",
  },
  {
    router: "/zones_manager",
    component: <ZonesManager />,
    menu_item_selected: "zones_manager",
  },
  {
    router: "/opening_hours",
    component: <OpeningHours />,
    menu_item_selected: "opening_hours",
  },
  {
    router: "/control_panel",
    component: <ControlPanel />,
    menu_item_selected: "control_panel",
  },
  {
    router: "/access_log",
    component: <AccessLog />,
    menu_item_selected: "access_log",
  },
  {
    router: "/cameras_monitor",
    component: <CamerasMonitor />,
    menu_item_selected: "cameras_monitor",
  },
  {
    router: "/lost_sales",
    component: <ListaVez />,
    menu_item_selected: "lost_sales",
  },
  {
    router: "/rscore_benchmarks",
    component: <RScoreBenchmarksManager />,
    menu_item_selected: "rscore_benchmarks",
  },
  {
    router: "/ai_reports",
    component: <AiReports />,
    menu_item_selected: "ai_reports",
  },
  {
    router: "/ai_agent",
    component: <AiAgent />,
    menu_item_selected: "ai_agent",
  },
  {
    router: "/contatos",
    component: <Contatos />,
    menu_item_selected: "contatos",
  },
  {
    router: "/gateway_cameras",
    component: <ConexaoCamerasGateway />,
    menu_item_selected: "gateway_cameras",
  },
];

const publicRoutes = [
  { router: "/login", component: <Login /> },
  { router: "/reset-password", component: <ResetPassword /> },
];

root.render(
  // <React.StrictMode>
  <QueryClientProvider client={queryClient}>
    {process.env.REACT_APP_ENV === "development" && (
      <ReactQueryDevtools initialIsOpen={true} />
    )}
    <EnvironmentBanner />
    <BrowserRouter>
      <AuthContextProvider>
        <Routes>
          <Route
            path="/"
            element={
              <PrivateRoute>
                <DefaultDashboardRoute />
              </PrivateRoute>
            }
          />
          {protectedRoutes.map((route) => (
            <Route
              key={route.router}
              path={route.router}
              element={
                <PrivateRoute>
                  <MenuBar menu_item_selected={route.menu_item_selected}>
                    {route.component}
                  </MenuBar>
                </PrivateRoute>
              }
            />
          ))}
          {publicRoutes.map((route) => (
            <Route
              key={route.router}
              path={route.router}
              element={route.component}
            />
          ))}
        </Routes>
      </AuthContextProvider>
    </BrowserRouter>
  </QueryClientProvider>
  // </React.StrictMode>
);
